import { API_ENDPOINTS, getApiUrl, getAuthHeaders, getCollectionListUrl } from "../config/api";
import type {
  InviteForm,
  InviteFormPatch,
  InvitePayInfo,
  InviteRequest,
  InviteRequestList,
  InviteRequestSubmit,
  InviteStatusView,
  InviteSubmitResult,
  PublicInviteMeta,
} from "../types/invite";
import {
  clearAuthToken,
  clearStoredUser,
  readAuthToken,
  readRefreshToken,
  writeAuthToken,
  writeRefreshToken,
} from "../utils/authStorage";
import { formatInr } from "../utils/currency";
import {
  Artist,
  AuthResponse,
  Budget,
  ChangePasswordPayload,
  Company,
  CompanyUserSummary,
  Contact,
  DashboardData,
  EventDetail,
  EventFinance,
  EventListItem,
  EventListPage,
  EventAiInsights,
  EventFinanceOverview,
  EventOSData,
  ForecastCurveResponse,
  TeamMember,
  TeamMemberCreate,
  TeamMemberUpdate,
  CompanyOperatorAccount,
  CompanyOperatorCreate,
  CompanyOperatorCredentialsPatch,
  EventPromoterAssignment,
  EventPromoterAssignmentCreateResult,
  EventPromoterAccountCreate,
  EventNote,
  EventDealTerms,
  EventDealTermsUpsert,
  EventFile,
  TicketEntryLog,
  TicketEntryLogCreate,
  TicketEntryLogUpdate,
  TicketEntryLogListParams,
  TicketEntryLogListResponse,
  TicketingPartner,
  TicketTier,
  TicketingLink,
  TicketingTierMapping,
  AdminCompanySummary,
  AdminCompanyEvent,
  TicketingCatalogEvent,
  GenerateInvoicePayload,
  InvoiceCalculationResponse,
  InvoiceDraftPayload,
  InvoiceRecord,
  InvoiceSearchGroup,
  InvoiceSortKey,
  NotificationPreferences,
  NotificationPreferencesUpdate,
  EventReview,
  PortfolioInsightsResponse,
  PortfolioMetricsResponse,
  PortfolioSummaryResponse,
  PortfolioTimeframe,
  PortfolioTransaction,
  PortfolioXAxisDimension,
  PortfolioYAxisMetric,
  Schedule,
  Staffing,
  Task,
  UiMetric,
  UpcomingPayout,
  User,
  UserProfilePatch,
  Venue,
} from "../types";

type JsonBody = Record<string, unknown>;
let authRecoveryTriggered = false;
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessTokenDirect(): Promise<AuthResponse | null> {
  const refreshToken = readRefreshToken();
  if (!refreshToken) return null;

  const response = await fetch(getApiUrl(API_ENDPOINTS.AUTH.REFRESH), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  const text = await response.text();
  if (!response.ok) return null;
  return text ? (JSON.parse(text) as AuthResponse) : null;
}

async function tryRefreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessTokenDirect()
      .then((response) => {
        if (!response) return null;
        writeAuthToken(response.access_token);
        if (response.refresh_token) {
          writeRefreshToken(response.refresh_token);
        }
        authRecoveryTriggered = false;
        return response.access_token;
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function formatApiErrorMessage(text: string, status: number): string {
  if (!text) return `Request failed with status ${status}`;
  try {
    const parsed = JSON.parse(text) as { detail?: unknown; message?: string };
    if (parsed.message) return String(parsed.message);
    if (parsed.detail !== undefined && parsed.detail !== null) {
      if (typeof parsed.detail === "string") return parsed.detail;
      if (Array.isArray(parsed.detail)) {
        return parsed.detail
          .map((item) => {
            if (typeof item === "string") return item;
            if (item && typeof item === "object" && "msg" in item) return String((item as { msg: string }).msg);
            return JSON.stringify(item);
          })
          .join("; ");
      }
      if (typeof parsed.detail === "object") {
        const detail = parsed.detail as { errors?: unknown; failed?: unknown };
        // Invoice gate / finalize: { errors: ["..."], missing_fields: [...] }
        if (Array.isArray(detail.errors) && detail.errors.length) {
          return detail.errors.map(String).join(" ");
        }
        // Bulk import: { failed: [{ row, name, error }] }
        if (Array.isArray(detail.failed) && detail.failed.length) {
          return detail.failed
            .map((f) => {
              const item = f as { row?: number; name?: string; error?: string };
              return `Row ${item.row ?? "?"}${item.name ? ` (${item.name})` : ""}: ${item.error ?? "invalid"}`;
            })
            .join("; ");
        }
        return JSON.stringify(parsed.detail);
      }
    }
  } catch {
    return text;
  }
  return text;
}

/** Thrown for non-2xx responses; keeps the parsed body so callers can read structured errors. */
export class ApiError extends Error {
  status: number;
  detail: unknown;

  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

function parseErrorDetail(text: string): unknown {
  try {
    return (JSON.parse(text) as { detail?: unknown }).detail;
  } catch {
    return undefined;
  }
}

/** Field-level messages from a FastAPI 422 ({loc: [..., "phone"], msg}) keyed by field name. */
export function fieldErrorsFromApiError(err: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!(err instanceof ApiError) || !Array.isArray(err.detail)) return out;
  for (const item of err.detail as Array<{ loc?: unknown[]; msg?: string }>) {
    const field = item?.loc?.[item.loc.length - 1];
    if (typeof field === "string" && item.msg && !(field in out)) {
      out[field] = item.msg.replace(/^Value error, /, "");
    }
  }
  return out;
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers ?? undefined);
  const token = readAuthToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const method = (options.method || "GET").toUpperCase();
  const hasAuthHeader = headers.has("Authorization");
  const hasStoredToken = Boolean(token);
  const isAuthEndpoint =
    url.includes(API_ENDPOINTS.AUTH.LOGIN) ||
    url.includes(API_ENDPOINTS.AUTH.REGISTER) ||
    url.includes(API_ENDPOINTS.AUTH.REFRESH);

  const isMutation = method !== "GET" && method !== "HEAD";
  let response = await fetch(url, {
    ...options,
    headers,
    // FastAPI slash-normalizes with 307. The browser would replay the POST;
    // some proxies also follow that redirect, which created the event twice.
    ...(isMutation ? { redirect: "manual" as RequestRedirect } : {}),
  });
  if (isMutation && (response.status === 307 || response.status === 308)) {
    const location = response.headers.get("Location");
    if (location) {
      response = await fetch(location, { ...options, headers, redirect: "manual" });
    }
  }
  let text = await response.text();

  if (response.status === 401 && hasAuthHeader && !isAuthEndpoint && readRefreshToken()) {
    const newToken = await tryRefreshAccessToken();
    if (newToken) {
      const retryHeaders = new Headers(options.headers ?? undefined);
      retryHeaders.set("Authorization", `Bearer ${newToken}`);
      response = await fetch(url, {
        ...options,
        headers: retryHeaders,
        ...(isMutation ? { redirect: "manual" as RequestRedirect } : {}),
      });
      text = await response.text();
    }
  }

  if (response.status === 401) {
    console.warn("[API 401]", { method, url, hasAuthHeader, hasStoredToken });
    if (hasAuthHeader && !authRecoveryTriggered && !isAuthEndpoint) {
      authRecoveryTriggered = true;
      clearAuthToken();
      clearStoredUser();
      if (!window.location.pathname.startsWith("/auth")) {
        window.location.replace("/auth");
      }
    }
  }
  if (!response.ok) {
    throw new ApiError(formatApiErrorMessage(text, response.status), response.status, parseErrorDetail(text));
  }
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

function authHeadersMultipart(token?: string): HeadersInit {
  const headers = new Headers();
  const authToken = token || readAuthToken();
  if (authToken) {
    headers.set("Authorization", `Bearer ${authToken}`);
  }
  return headers;
}

/**
 * GET a binary response with the same 401 → refresh-token → retry behaviour
 * as `request()`. Kept separate because `request()` always JSON-parses the
 * body, which would corrupt a downloaded file.
 */
async function fetchBlobWithAuth(url: string, token?: string): Promise<Blob> {
  let response = await fetch(url, { method: "GET", headers: authHeadersMultipart(token) });
  if (response.status === 401 && readRefreshToken()) {
    const newToken = await tryRefreshAccessToken();
    if (newToken) {
      response = await fetch(url, { method: "GET", headers: authHeadersMultipart(newToken) });
    }
  }
  if (!response.ok) {
    throw new Error(formatApiErrorMessage(await response.text(), response.status));
  }
  return response.blob();
}

export const authApi = {
  register: (payload: JsonBody) =>
    request<AuthResponse>(getApiUrl(API_ENDPOINTS.AUTH.REGISTER), {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }),
  login: (payload: JsonBody) =>
    request<AuthResponse>(getApiUrl(API_ENDPOINTS.AUTH.LOGIN), {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }),
  refresh: (refreshToken: string) =>
    request<AuthResponse>(getApiUrl(API_ENDPOINTS.AUTH.REFRESH), {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
  logout: (refreshToken?: string | null) =>
    request<{ message: string }>(getApiUrl(API_ENDPOINTS.AUTH.LOGOUT), {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ refresh_token: refreshToken ?? null }),
    }),
  forgotPassword: (email: string) =>
    request<{ message: string }>(getApiUrl(API_ENDPOINTS.AUTH.FORGOT_PASSWORD), {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ message: string }>(getApiUrl(API_ENDPOINTS.AUTH.RESET_PASSWORD), {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
};

export const usersApi = {
  me: (token?: string) =>
    request<User>(getApiUrl(API_ENDPOINTS.USERS.ME), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  updateProfile: async (payload: UserProfilePatch, token?: string): Promise<User> => {
    const raw = await request<unknown>(getApiUrl(API_ENDPOINTS.USERS.ME), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    });
    if (raw && typeof raw === "object" && raw !== null && "id" in raw && typeof (raw as User).id === "string") {
      return raw as User;
    }
    return usersApi.me(token);
  },
  changePassword: (payload: ChangePasswordPayload, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.USERS.ME_PASSWORD), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  uploadAvatar: async (file: File, token?: string): Promise<User> => {
    const body = new FormData();
    body.append("file", file);
    const raw = await request<unknown>(getApiUrl(API_ENDPOINTS.USERS.ME_AVATAR), {
      method: "POST",
      headers: authHeadersMultipart(token),
      body,
    });
    if (raw && typeof raw === "object" && raw !== null && "id" in raw && typeof (raw as User).id === "string") {
      return raw as User;
    }
    return usersApi.me(token);
  },
  companyMembers: (
    params: { search?: string; exclude_event_id?: string; limit?: number } = {},
    token?: string,
  ) => {
    const qs = new URLSearchParams();
    if (params.search && params.search.trim()) qs.set("search", params.search.trim());
    if (params.exclude_event_id) qs.set("exclude_event_id", params.exclude_event_id);
    if (params.limit !== undefined) qs.set("limit", String(params.limit));
    const query = qs.toString();
    const path = `${API_ENDPOINTS.USERS.COMPANY}${query ? `?${query}` : ""}`;
    return request<CompanyUserSummary[]>(getApiUrl(path), {
      method: "GET",
      headers: getAuthHeaders(token),
    });
  },
};

export const notificationsApi = {
  getPreferences: (token?: string) =>
    request<NotificationPreferences>(getApiUrl(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  updatePreferences: (payload: NotificationPreferencesUpdate, token?: string) =>
    request<NotificationPreferences>(getApiUrl(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  requestWhatsAppCode: (phone: string, token?: string) =>
    request<NotificationPreferences>(getApiUrl(API_ENDPOINTS.NOTIFICATIONS.WHATSAPP_VERIFY_REQUEST), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify({ phone }),
    }),
  verifyWhatsAppCode: (code: string, consentText: string, token?: string) =>
    request<NotificationPreferences>(getApiUrl(API_ENDPOINTS.NOTIFICATIONS.WHATSAPP_VERIFY), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify({ code, consent_text: consentText }),
    }),
  revokeWhatsApp: (token?: string) =>
    request<NotificationPreferences>(getApiUrl(API_ENDPOINTS.NOTIFICATIONS.WHATSAPP), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
};

export const eventsApi = {
  /** Returns a flat event array. Backend returns EventListPage; older shapes may be bare arrays. */
  list: async (token?: string): Promise<EventListItem[]> => {
    const base = getCollectionListUrl(API_ENDPOINTS.EVENTS.LIST);
    const pageSize = 100;
    const items: EventListItem[] = [];
    let page = 1;
    let total = Infinity;

    while (items.length < total) {
      const sep = base.includes("?") ? "&" : "?";
      const url = `${base}${sep}page=${page}&page_size=${pageSize}&sort=date_asc`;
      const data = await request<EventListPage | EventListItem[]>(url, {
        method: "GET",
        headers: getAuthHeaders(token),
      });

      if (Array.isArray(data)) {
        return data;
      }

      const batch = Array.isArray(data?.items) ? data.items : [];
      items.push(...batch);
      total = typeof data?.total === "number" ? data.total : items.length;
      if (batch.length === 0) break;
      page += 1;
      if (page > 50) break;
    }

    return items;
  },
  /** Atomic bulk create. On row failures the server answers 422 with detail.failed = [{row, name, error}]. */
  importRows: (rows: JsonBody[], token?: string) =>
    request<{ created: number; failed: { row: number; name: string; error: string }[]; event_ids: string[] }>(
      getApiUrl(API_ENDPOINTS.EVENTS.IMPORT),
      { method: "POST", headers: getAuthHeaders(token), body: JSON.stringify({ rows }) },
    ),
  statuses: (token?: string) =>
    request<EventStatusInfo[]>(getApiUrl(API_ENDPOINTS.EVENTS.STATUSES), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (payload: JsonBody, token?: string) =>
    request<EventDetail>(getApiUrl(API_ENDPOINTS.EVENTS.CREATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  detail: (eventId: string, token?: string) =>
    request<EventDetail>(getApiUrl(API_ENDPOINTS.EVENTS.DETAIL(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  calculatePnl: (payload: JsonBody, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.EVENTS.CALCULATE_PNL), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  dashboard: (eventId: string, token?: string) =>
    request<DashboardData>(getApiUrl(API_ENDPOINTS.EVENTS.DASHBOARD(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  financeOverview: (eventId: string, token?: string) =>
    request<EventFinanceOverview>(getApiUrl(API_ENDPOINTS.EVENTS.FINANCE_OVERVIEW(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  /**
   * Whole sell-through curve (0-100%) for the P&L scenario slider. Fetched
   * once so dragging resolves locally instead of per-frame requests.
   */
  forecastCurve: (eventId: string, token?: string) =>
    request<ForecastCurveResponse>(getApiUrl(API_ENDPOINTS.EVENTS.FORECAST_CURVE(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  aiInsights: (eventId: string, token?: string) =>
    request<EventAiInsights>(getApiUrl(API_ENDPOINTS.EVENTS.AI_INSIGHTS(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  staffingEstimate: (payload: JsonBody, token?: string) =>
    request<Staffing>(getApiUrl(API_ENDPOINTS.EVENTS.STAFFING_ESTIMATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  update: (eventId: string, payload: JsonBody, token?: string) =>
    request<EventDetail>(getApiUrl(API_ENDPOINTS.EVENTS.UPDATE(eventId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  replace: (eventId: string, payload: JsonBody, token?: string) =>
    request<EventDetail>(getApiUrl(API_ENDPOINTS.EVENTS.UPDATE(eventId)), {
      method: "PUT",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  remove: (eventId: string, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.EVENTS.DELETE(eventId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  review: (eventId: string, token?: string) =>
    request<EventReview>(getApiUrl(API_ENDPOINTS.EVENTS.REVIEW(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  refreshReview: (eventId: string, token?: string) =>
    request<EventReview>(getApiUrl(API_ENDPOINTS.EVENTS.REVIEW_REFRESH(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
    }),
};

export const ticketTiersApi = {
  update: (eventId: string, tierId: string, payload: JsonBody, token?: string) =>
    request<TicketTier>(getApiUrl(API_ENDPOINTS.EVENTS.TICKET_TIER(eventId, tierId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  create: (eventId: string, payload: JsonBody, token?: string) =>
    request<TicketTier>(getApiUrl(API_ENDPOINTS.EVENTS.TICKET_TIERS(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  remove: (eventId: string, tierId: string, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.EVENTS.TICKET_TIER(eventId, tierId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
};

export const eventFinancesApi = {
  update: (eventId: string, payload: JsonBody, token?: string) =>
    request<EventFinance>(getApiUrl(API_ENDPOINTS.EVENTS.FINANCES(eventId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
};

const emptyEventOSData: EventOSData = {
  eventName: "No event selected",
  eventSubtitle: "Awaiting API data",
  eventHealthScore: null,
  overviewMetrics: [],
  pnlMetrics: [],
  ticketMetrics: [],
  venueMetrics: [],
  workforceMetrics: [],
  vendorMetrics: [],
  taskMetrics: [],
  riskMetrics: [],
  liveMetrics: [],
  ticketTiers: [],
  scenarioMetrics: [],
  reportMetrics: [],
  alerts: [],
  insights: [],
  ticketTable: { headers: [], rows: [] },
  workforceTable: { headers: [], rows: [] },
  vendorTable: { headers: [], rows: [] },
  riskTable: { headers: [], rows: [] },
  revenueChart: [],
  expenseChart: [],
  ticketTierChart: [],
  scenarioChart: [],
};

const formatCurrency = (value: number): string => formatInr(value, { maximumFractionDigits: 2 });
const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)}%`;
};

type MetricTone = NonNullable<UiMetric["tone"]>;

function toneRevenueAmount(n: number): MetricTone {
  return n > 0 ? "success" : "info";
}

function toneCostAmount(n: number): MetricTone {
  return n > 0 ? "danger" : "info";
}

function toneSignedAmount(n: number | null | undefined): MetricTone {
  if (n === null || n === undefined || !Number.isFinite(n)) return "info";
  if (n > 0) return "success";
  if (n < 0) return "danger";
  return "info";
}

function toneSoldVsBreakEven(sold: number, breakEven: number): MetricTone {
  if (breakEven <= 0) return "info";
  return sold >= breakEven ? "success" : "danger";
}

export const companiesApi = {
  me: (token?: string) =>
    request<Company>(getApiUrl(API_ENDPOINTS.COMPANIES.ME), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  update: (
    payload: Partial<
      Pick<
        Company,
        | "name"
        | "default_ticket_template_id"
        | "invoice_gstin"
        | "invoice_address"
        | "invoice_state_name"
        | "invoice_state_code"
        | "bank_account_holder"
        | "bank_account_number"
        | "bank_ifsc_code"
        | "bank_branch"
      >
    >,
    token?: string
  ) =>
    request<Company>(getApiUrl(API_ENDPOINTS.COMPANIES.UPDATE), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  uploadLogo: (file: File, token?: string): Promise<Company> => {
    const body = new FormData();
    body.append("file", file);
    return request<Company>(getApiUrl(API_ENDPOINTS.COMPANIES.LOGO), {
      method: "POST",
      headers: authHeadersMultipart(token),
      body,
    });
  },
  deleteLogo: (token?: string) =>
    request<Company>(getApiUrl(API_ENDPOINTS.COMPANIES.LOGO), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  /** Follows the backend's redirect to a presigned S3 URL and returns the file bytes. */
  downloadLogo: (token?: string): Promise<Blob> => fetchBlobWithAuth(getApiUrl(API_ENDPOINTS.COMPANIES.LOGO), token),
  uploadInvoiceTemplate: (file: File, token?: string): Promise<Company> => {
    const body = new FormData();
    body.append("file", file);
    return request<Company>(getApiUrl(API_ENDPOINTS.COMPANIES.INVOICE_TEMPLATE), {
      method: "POST",
      headers: authHeadersMultipart(token),
      body,
    });
  },
  deleteInvoiceTemplate: (token?: string) =>
    request<Company>(getApiUrl(API_ENDPOINTS.COMPANIES.INVOICE_TEMPLATE), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  downloadInvoiceTemplate: (token?: string): Promise<Blob> =>
    fetchBlobWithAuth(getApiUrl(API_ENDPOINTS.COMPANIES.INVOICE_TEMPLATE), token),
};

export const artistsApi = {
  list: (token?: string) =>
    request<Artist[]>(getApiUrl(API_ENDPOINTS.ARTISTS.LIST), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  detail: (id: string, token?: string) =>
    request<Artist>(getApiUrl(API_ENDPOINTS.ARTISTS.DETAIL(id)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (payload: JsonBody, token?: string) =>
    request<Artist>(getApiUrl(API_ENDPOINTS.ARTISTS.CREATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  update: (id: string, payload: JsonBody, token?: string) =>
    request<Artist>(getApiUrl(API_ENDPOINTS.ARTISTS.UPDATE(id)), {
      method: "PUT",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  remove: (id: string, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.ARTISTS.DELETE(id)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  uploadPhoto: (id: string, file: File, token?: string): Promise<Artist> => {
    const body = new FormData();
    body.append("file", file);
    return request<Artist>(getApiUrl(API_ENDPOINTS.ARTISTS.PHOTO(id)), {
      method: "POST",
      headers: authHeadersMultipart(token),
      body,
    });
  },
};

export const venuesApi = {
  list: (token?: string) =>
    request<Venue[]>(getApiUrl(API_ENDPOINTS.VENUES.LIST), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  detail: (id: string, token?: string) =>
    request<Venue>(getApiUrl(API_ENDPOINTS.VENUES.DETAIL(id)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (payload: JsonBody, token?: string) =>
    request<Venue>(getApiUrl(API_ENDPOINTS.VENUES.CREATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  update: (id: string, payload: JsonBody, token?: string) =>
    request<Venue>(getApiUrl(API_ENDPOINTS.VENUES.UPDATE(id)), {
      method: "PUT",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  remove: (id: string, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.VENUES.DELETE(id)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  uploadPhoto: (id: string, file: File, token?: string): Promise<Venue> => {
    const body = new FormData();
    body.append("file", file);
    return request<Venue>(getApiUrl(API_ENDPOINTS.VENUES.PHOTO(id)), {
      method: "POST",
      headers: authHeadersMultipart(token),
      body,
    });
  },
};

export const contactsApi = {
  list: (token?: string, search?: string) => {
    const base = getApiUrl(API_ENDPOINTS.CONTACTS.LIST);
    const url = search?.trim() ? `${base}?search=${encodeURIComponent(search.trim())}` : base;
    return request<Contact[]>(url, {
      method: "GET",
      headers: getAuthHeaders(token),
    });
  },
  detail: (id: string, token?: string) =>
    request<Contact>(getApiUrl(API_ENDPOINTS.CONTACTS.DETAIL(id)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (payload: JsonBody, token?: string) =>
    request<Contact>(getApiUrl(API_ENDPOINTS.CONTACTS.CREATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  update: (id: string, payload: JsonBody, token?: string) =>
    request<Contact>(getApiUrl(API_ENDPOINTS.CONTACTS.UPDATE(id)), {
      method: "PUT",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  remove: (id: string, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.CONTACTS.DELETE(id)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  linkArtists: (id: string, artistIds: string[], token?: string) =>
    request<Contact>(getApiUrl(API_ENDPOINTS.CONTACTS.LINK_ARTISTS(id)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify({ artist_ids: artistIds }),
    }),
  unlinkArtist: (id: string, artistId: string, token?: string) =>
    request<Contact>(getApiUrl(API_ENDPOINTS.CONTACTS.UNLINK_ARTIST(id, artistId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  uploadPhoto: (id: string, file: File, token?: string): Promise<Contact> => {
    const body = new FormData();
    body.append("file", file);
    return request<Contact>(getApiUrl(API_ENDPOINTS.CONTACTS.PHOTO(id)), {
      method: "POST",
      headers: authHeadersMultipart(token),
      body,
    });
  },
};

export const budgetsApi = {
  list: (eventId: string, token?: string) =>
    request<Budget[]>(getApiUrl(API_ENDPOINTS.BUDGETS.LIST(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (eventId: string, payload: JsonBody, token?: string) =>
    request<Budget>(getApiUrl(API_ENDPOINTS.BUDGETS.CREATE(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  update: (eventId: string, budgetId: string, payload: JsonBody, token?: string) =>
    request<Budget>(getApiUrl(API_ENDPOINTS.BUDGETS.UPDATE(eventId, budgetId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  remove: (eventId: string, budgetId: string, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.BUDGETS.DELETE(eventId, budgetId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
};

export const tasksApi = {
  list: (eventId: string, token?: string) =>
    request<Task[]>(getApiUrl(API_ENDPOINTS.TASKS.LIST(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (eventId: string, payload: JsonBody, token?: string) =>
    request<Task>(getApiUrl(API_ENDPOINTS.TASKS.CREATE(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  update: (eventId: string, taskId: string, payload: JsonBody, token?: string) =>
    request<Task>(getApiUrl(API_ENDPOINTS.TASKS.UPDATE(eventId, taskId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  remove: (eventId: string, taskId: string, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.TASKS.DELETE(eventId, taskId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
};

export const schedulesApi = {
  list: (eventId: string, token?: string) =>
    request<Schedule[]>(getApiUrl(API_ENDPOINTS.SCHEDULES.LIST(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (eventId: string, payload: JsonBody, token?: string) =>
    request<Schedule>(getApiUrl(API_ENDPOINTS.SCHEDULES.CREATE(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  update: (eventId: string, scheduleId: string, payload: JsonBody, token?: string) =>
    request<Schedule>(getApiUrl(API_ENDPOINTS.SCHEDULES.UPDATE(eventId, scheduleId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  remove: (eventId: string, scheduleId: string, token?: string) =>
    request<Record<string, unknown>>(getApiUrl(API_ENDPOINTS.SCHEDULES.DELETE(eventId, scheduleId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
};

export const invoicesApi = {
  generate: (payload: GenerateInvoicePayload, token?: string) =>
    request<InvoiceCalculationResponse>(getApiUrl(API_ENDPOINTS.INVOICES.GENERATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  detail: (id: string, token?: string) =>
    request<InvoiceRecord>(getApiUrl(API_ENDPOINTS.INVOICES.DETAIL(id)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  listByEvent: (eventId: string, token?: string) =>
    request<InvoiceRecord[]>(getApiUrl(API_ENDPOINTS.INVOICES.BY_EVENT(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  search: (
    params: {
      q?: string;
      event_id?: string;
      status?: string;
      sort?: InvoiceSortKey;
      include_drafts?: boolean;
    },
    token?: string
  ) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.event_id) qs.set("event_id", params.event_id);
    if (params.status) qs.set("status", params.status);
    if (params.sort) qs.set("sort", params.sort);
    if (params.include_drafts === false) qs.set("include_drafts", "false");
    const query = qs.toString();
    return request<InvoiceSearchGroup[]>(getApiUrl(`${API_ENDPOINTS.INVOICES.SEARCH}${query ? `?${query}` : ""}`), {
      method: "GET",
      headers: getAuthHeaders(token),
    });
  },
  updateStatus: (invoiceId: string, status: string, token?: string) =>
    request<InvoiceRecord>(getApiUrl(API_ENDPOINTS.INVOICES.STATUS(invoiceId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify({ status }),
    }),
  /** Save an incomplete invoice. Only event_id is required. */
  createDraft: (payload: InvoiceDraftPayload, token?: string) =>
    request<InvoiceRecord>(getApiUrl(API_ENDPOINTS.INVOICES.DRAFTS), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  updateDraft: (invoiceId: string, payload: InvoiceDraftPayload, token?: string) =>
    request<InvoiceRecord>(getApiUrl(API_ENDPOINTS.INVOICES.UPDATE(invoiceId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  /** Issue a draft — validates, then assigns the sequential number. */
  finalizeDraft: (invoiceId: string, token?: string) =>
    request<InvoiceRecord>(getApiUrl(API_ENDPOINTS.INVOICES.FINALIZE(invoiceId)), {
      method: "POST",
      headers: getAuthHeaders(token),
    }),
  deleteDraft: (invoiceId: string, token?: string) =>
    request<void>(getApiUrl(API_ENDPOINTS.INVOICES.DELETE(invoiceId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  uploadPdf: (invoiceId: string, blob: Blob, token?: string): Promise<InvoiceRecord> => {
    const body = new FormData();
    body.append("file", blob, "invoice.pdf");
    return request<InvoiceRecord>(getApiUrl(API_ENDPOINTS.INVOICES.PDF(invoiceId)), {
      method: "POST",
      headers: authHeadersMultipart(token),
      body,
    });
  },
  email: (invoiceId: string, toEmail: string, pdfBase64: string, token?: string) =>
    request<InvoiceRecord>(getApiUrl(API_ENDPOINTS.INVOICES.EMAIL(invoiceId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify({ to_email: toEmail, pdf_base64: pdfBase64 }),
    }),
  bulkDownload: async (invoiceIds: string[], token?: string): Promise<Blob | { missing_pdf_invoice_ids: string[] }> => {
    const url = getApiUrl(API_ENDPOINTS.INVOICES.BULK_DOWNLOAD);
    const response = await fetch(url, {
      method: "POST",
      headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify({ invoice_ids: invoiceIds }),
    });
    const contentType = response.headers.get("content-type") || "";
    if (!response.ok) {
      const text = await response.text();
      throw new Error(formatApiErrorMessage(text, response.status));
    }
    if (contentType.includes("application/json")) {
      return (await response.json()) as { missing_pdf_invoice_ids: string[] };
    }
    return response.blob();
  },
};

export type EventStatusInfo = {
  value: string;
  label: string;
  is_draft: boolean;
  is_cancelled: boolean;
  is_active: boolean;
  hold_level: number | null;
  selectable: boolean;
};

export type PincodeInfo = {
  pincode: string;
  state: string | null;
  state_matched: boolean;
  raw_state: string | null;
  district: string | null;
  cities: string[];
};

export const geoApi = {
  pincode: (pin: string, token?: string) =>
    request<PincodeInfo>(getApiUrl(API_ENDPOINTS.GEO.PINCODE(pin)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
};

export type PortfolioMetricsParams = {
  y: PortfolioYAxisMetric[];
  x?: PortfolioXAxisDimension | null;
  timeframe: PortfolioTimeframe;
  /** Whole periods back from today (0 = current period, negative = forward). */
  offset?: number;
};

export const portfolioApi = {
  metrics: (params: PortfolioMetricsParams, token?: string) => {
    const qs = new URLSearchParams();
    qs.set("y", params.y.join(","));
    if (params.x) qs.set("x", params.x);
    qs.set("timeframe", params.timeframe);
    if (params.offset) qs.set("offset", String(params.offset));
    return request<PortfolioMetricsResponse>(getApiUrl(`${API_ENDPOINTS.PORTFOLIO.METRICS}?${qs.toString()}`), {
      method: "GET",
      headers: getAuthHeaders(token),
    });
  },
  summary: (timeframe: PortfolioTimeframe, token?: string, offset = 0) =>
    request<PortfolioSummaryResponse>(getApiUrl(`${API_ENDPOINTS.PORTFOLIO.SUMMARY}?timeframe=${timeframe}&offset=${offset}`), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  insights: (metric: PortfolioYAxisMetric, token?: string) =>
    request<PortfolioInsightsResponse>(getApiUrl(`${API_ENDPOINTS.PORTFOLIO.INSIGHTS}?metric=${metric}`), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  upcomingPayouts: (token?: string) =>
    request<UpcomingPayout[]>(getApiUrl(API_ENDPOINTS.PORTFOLIO.UPCOMING_PAYOUTS), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  transactions: (token?: string) =>
    request<PortfolioTransaction[]>(getApiUrl(API_ENDPOINTS.PORTFOLIO.TRANSACTIONS), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
};

async function fetchDoorEntriesQuantity(eventId: string, token?: string): Promise<number | null> {
  try {
    let total = 0;
    let cursor: string | undefined;
    for (let i = 0; i < 20; i += 1) {
      const page = await ticketEntriesApi.list(
        eventId,
        { limit: 200, direction: "desc", ...(cursor ? { cursor } : {}) },
        token,
      );
      if (!page.items.length) break;
      total += page.items.reduce((sum, entry) => sum + Number(entry.quantity_sold || 0), 0);
      if (!page.next_cursor) break;
      cursor = page.next_cursor;
    }
    return total;
  } catch {
    return null;
  }
}

export const eventOsApi = {
  listEvents: (token?: string) => eventsApi.list(token),
  createEvent: (payload: JsonBody, token?: string) => eventsApi.create(payload, token),
  updateEvent: (eventId: string, payload: JsonBody, token?: string) => eventsApi.update(eventId, payload, token),
  deleteEvent: (eventId: string, token?: string) => eventsApi.remove(eventId, token),
  getDashboardData: async (eventId: string, token?: string): Promise<EventOSData> => {
    if (!token || !eventId) return emptyEventOSData;

    const [eventDetail, dashboard] = await Promise.all([eventsApi.detail(eventId, token), eventsApi.dashboard(eventId, token)]);
    // Logistics costs live in the budgets table; the dashboard endpoint already
    // returns a logistics-inclusive `total_cost`, so derive the logistics
    // portion by subtracting the three event-finance buckets and pass it into
    // the stateless /calculate-pnl call so its totals stay consistent.
    const productionCost = eventDetail.finances?.production_cost ?? 0;
    const marketingCost = eventDetail.finances?.marketing_cost ?? 0;
    const artistFeeCost = eventDetail.finances?.artist_fee ?? 0;
    const dashboardTotalCost = Number(dashboard.metrics.total_cost ?? 0);
    const logisticsCost = Math.max(
      0,
      dashboardTotalCost - productionCost - marketingCost - artistFeeCost,
    );
    const pnl = await eventsApi.calculatePnl(
      {
        venue_capacity: eventDetail.venue_capacity,
        ticket_tiers: eventDetail.ticket_tiers.map((tier) => ({
          tier_name: tier.tier_name,
          price: tier.price,
          quantity: tier.quantity,
        })),
        production_cost: productionCost,
        marketing_cost: marketingCost,
        artist_fee: artistFeeCost,
        logistics_cost: logisticsCost,
      },
      token
    );

    const review = dashboard.review;
    // Projected (full sell-through) numbers come from event.finances / the PnL engine
    // and are independent of how many tickets have actually been sold so far.
    const projectedRevenue = Number(dashboard.metrics.projected_revenue ?? 0);
    const totalCost = Number(dashboard.metrics.total_cost ?? 0);
    const projectedProfit = Number(dashboard.metrics.profit ?? projectedRevenue - totalCost);
    // Margins are null when revenue is 0 — see BE-1 / FE-1. Preserve null so
    // formatPercent can render "—" rather than misleading "0.0%".
    const projectedProfitMargin: number | null =
      dashboard.metrics.profit_margin === null || dashboard.metrics.profit_margin === undefined
        ? null
        : Number(dashboard.metrics.profit_margin);

    // Actual (realized) numbers track tickets sold and update with live ops.
    // Prefer the persisted review row (which is recomputed on every dashboard
    // fetch) and fall back to the new metrics fields when available.
    const actualRevenue = Number(
      review?.total_revenue ??
        dashboard.metrics.actual_revenue ??
        dashboard.ticket_summary.revenue_generated ??
        0,
    );
    const actualProfit = Number(
      review?.net_profit ?? dashboard.metrics.actual_profit ?? (actualRevenue - totalCost),
    );
    const actualProfitMargin: number | null = (() => {
      const raw = dashboard.metrics.actual_profit_margin;
      if (raw !== null && raw !== undefined) return Number(raw);
      return actualRevenue > 0 ? (actualProfit / actualRevenue) * 100 : null;
    })();

    const pnlProjectedRevenue = Number(pnl.projected_revenue ?? projectedRevenue);
    const pnlTotalCost = Number(pnl.total_cost ?? totalCost);
    const pnlProfit = Number(pnl.profit ?? projectedProfit);
    const pnlProfitMargin: number | null =
      pnl.profit_margin === null || pnl.profit_margin === undefined
        ? projectedProfitMargin
        : Number(pnl.profit_margin);
    const breakEvenTickets = Number(dashboard.metrics.break_even_tickets || 0);
    const ticketsSold = Number(dashboard.ticket_summary.tickets_sold || 0);
    const totalTickets = Number(dashboard.ticket_summary.total_tickets || 0);
    const remainingTickets = Number(dashboard.ticket_summary.remaining || 0);
    const sellThrough = Number(dashboard.ticket_summary.sell_through_pct || 0);

    const revenueChartPoints =
      dashboard.chart_data.revenue_by_tier.length > 0
        ? dashboard.chart_data.revenue_by_tier.map((item) => ({
            name: item.tier ?? item.tier_name ?? "Tier",
            value: Number(item.revenue) || 0,
          }))
        : [{ name: "Projected revenue", value: projectedRevenue }];

    const scenarioChartRows =
      dashboard.chart_data.forecast.length > 0
        ? dashboard.chart_data.forecast.map((item) => {
            const label = item.sell_through ?? item.scenario ?? "";
            const revenue = Number(item.revenue ?? item.value ?? 0) || 0;
            const profitValue =
              item.profit !== undefined && item.profit !== null
                ? Number(item.profit) || 0
                : revenue - totalCost;
            return {
              name: String(label),
              revenue,
              expenses: totalCost,
              netProfit: profitValue,
            };
          })
        : [
            {
              name: "Current model",
              revenue: projectedRevenue,
              expenses: totalCost,
              netProfit: projectedRevenue - totalCost,
            },
          ];

    const ticketTierPoints =
      dashboard.ticket_summary.tiers.length > 0
        ? dashboard.ticket_summary.tiers.map((tier) => ({
            name: tier.name,
            value: Number(tier.sold) || 0,
          }))
        : [{ name: "Tickets sold", value: ticketsSold }];

    const doorEntriesFromLogs = await fetchDoorEntriesQuantity(eventId, token);
    const doorEntries = Number(
      doorEntriesFromLogs ?? dashboard.metrics.door_entries ?? 0,
    );
    const teamOnGate = Number(dashboard.metrics.team_on_gate ?? 0);
    const healthScore =
      dashboard.metrics.health_score !== undefined && dashboard.metrics.health_score !== null
        ? Number(dashboard.metrics.health_score)
        : null;

    const riskAssessment = dashboard.risk_assessment ?? null;
    const riskLevel = riskAssessment?.overall_level
      ? String(riskAssessment.overall_level)
      : "unknown";
    const riskLevelLabel = riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1);
    const riskLevelTone: MetricTone =
      riskLevel === "critical" || riskLevel === "high"
        ? "danger"
        : riskLevel === "medium"
          ? "warning"
          : riskLevel === "low"
            ? "success"
            : "info";

    const staffing = dashboard.staffing
      ? dashboard.staffing
      : await eventsApi.staffingEstimate(
          {
            expected_attendees: Math.max(ticketsSold, 1),
            venue_capacity: Math.max(eventDetail.venue_capacity, 1),
          },
          token
        );

    return {
      ...emptyEventOSData,
      eventName: eventDetail.name,
      eventSubtitle: `${eventDetail.city} - ${eventDetail.event_date}`,
      eventHealthScore: healthScore,
      overviewMetrics: [
        { label: "Actual Revenue", value: formatCurrency(actualRevenue), tone: toneRevenueAmount(actualRevenue) },
        { label: "Actual Profit", value: formatCurrency(actualProfit), tone: toneSignedAmount(actualProfit) },
        { label: "Actual Margin", value: formatPercent(actualProfitMargin), tone: toneSignedAmount(actualProfitMargin) },
        { label: "Projected Revenue", value: formatCurrency(projectedRevenue), tone: toneRevenueAmount(projectedRevenue) },
        { label: "Total Cost", value: formatCurrency(totalCost), tone: toneCostAmount(totalCost) },
        { label: "Break-even Tickets", value: breakEvenTickets.toLocaleString("en-IN"), tone: "info" },
      ],
      pnlMetrics: [
        { label: "Actual Revenue", value: formatCurrency(actualRevenue), tone: toneRevenueAmount(actualRevenue) },
        { label: "Actual Profit", value: formatCurrency(actualProfit), tone: toneSignedAmount(actualProfit) },
        { label: "Actual Margin", value: formatPercent(actualProfitMargin), tone: toneSignedAmount(actualProfitMargin) },
        { label: "Projected Revenue", value: formatCurrency(pnlProjectedRevenue), tone: toneRevenueAmount(pnlProjectedRevenue) },
        { label: "Total Cost", value: formatCurrency(pnlTotalCost), tone: toneCostAmount(pnlTotalCost) },
        { label: "Projected Profit", value: formatCurrency(pnlProfit), tone: toneSignedAmount(pnlProfit) },
        { label: "Projected Margin", value: formatPercent(pnlProfitMargin), tone: toneSignedAmount(pnlProfitMargin) },
      ],
      ticketMetrics: [
        { label: "Total Tickets", value: totalTickets.toLocaleString("en-IN") },
        { label: "Tickets Sold", value: ticketsSold.toLocaleString("en-IN") },
        { label: "Remaining", value: remainingTickets.toLocaleString("en-IN") },
        { label: "Sell Through", value: formatPercent(sellThrough) },
      ],
      venueMetrics: [
        { label: "Venue Capacity", value: eventDetail.venue_capacity.toLocaleString("en-IN") },
        { label: "Tickets Sold", value: ticketsSold.toLocaleString("en-IN") },
        { label: "Remaining", value: remainingTickets.toLocaleString("en-IN") },
        { label: "Sell Through", value: formatPercent(sellThrough) },
      ],
      workforceMetrics: [
        { label: "Expected Attendees", value: staffing.expected_attendees.toLocaleString("en-IN") },
        { label: "Security Required", value: staffing.security_required.toLocaleString("en-IN") },
        { label: "Volunteers Required", value: staffing.volunteers_required.toLocaleString("en-IN") },
        { label: "Medics Required", value: staffing.medics_required.toLocaleString("en-IN") },
      ],
      vendorMetrics: [
        { label: "Financing Cost Head", value: formatCurrency(totalCost), tone: toneCostAmount(totalCost) },
        { label: "Projected Revenue", value: formatCurrency(projectedRevenue), tone: toneRevenueAmount(projectedRevenue) },
        {
          label: "Revenue Gap",
          value: formatCurrency(Math.max(totalCost - actualRevenue, 0)),
          tone: totalCost - actualRevenue > 0 ? "danger" : "info",
        },
        { label: "Actual Profit", value: formatCurrency(actualProfit), tone: toneSignedAmount(actualProfit) },
      ],
      taskMetrics: [
        { label: "Event Status", value: eventDetail.status },
        { label: "Event Date", value: eventDetail.event_date },
        { label: "City", value: eventDetail.city },
        { label: "Ticket Tiers", value: eventDetail.ticket_tiers.length.toString() },
      ],
      riskMetrics: [
        { label: "Break-even Tickets", value: breakEvenTickets.toLocaleString("en-IN"), tone: "info" },
        { label: "Tickets Sold", value: ticketsSold.toLocaleString("en-IN"), tone: toneSoldVsBreakEven(ticketsSold, breakEvenTickets) },
        { label: "Actual Margin", value: formatPercent(actualProfitMargin), tone: toneSignedAmount(actualProfitMargin) },
        {
          label: "Risk Level",
          value: riskLevelLabel,
          tone: riskLevelTone,
        },
      ],
      liveMetrics: [
        { label: "Tickets Sold", value: ticketsSold.toLocaleString("en-IN"), tone: toneSoldVsBreakEven(ticketsSold, breakEvenTickets) },
        {
          label: "Revenue Generated",
          value: formatCurrency(actualRevenue),
          tone: toneRevenueAmount(actualRevenue),
        },
        {
          label: "Actual Profit",
          value: formatCurrency(actualProfit),
          tone: toneSignedAmount(actualProfit),
        },
        { label: "Current Capacity", value: formatPercent(sellThrough) },
        { label: "Door Entries", value: doorEntries.toLocaleString("en-IN"), tone: "info" },
        { label: "Team On Gate", value: teamOnGate.toLocaleString("en-IN"), tone: "info" },
      ],
      ticketTiers: dashboard.ticket_summary.tiers
        .map((tier) => ({
          tier_id: tier.tier_id,
          name: tier.name,
          price: tier.price,
          quantity: tier.quantity,
          sold: tier.sold,
        }))
        .filter((tier) => Boolean(tier.tier_id)),
      ticketTable: {
        headers: ["Tier", "Price", "Quantity", "Sold", "Revenue"],
        rows: dashboard.ticket_summary.tiers.map((tier) => [
          tier.name,
          formatCurrency(tier.price),
          tier.quantity.toLocaleString("en-IN"),
          tier.sold.toLocaleString("en-IN"),
          formatCurrency(tier.revenue),
        ]),
      },
      workforceTable: {
        headers: ["Role", "Required"],
        rows: [
          ["Security", staffing.security_required.toLocaleString("en-IN")],
          ["Volunteers", staffing.volunteers_required.toLocaleString("en-IN")],
          ["Medics", staffing.medics_required.toLocaleString("en-IN")],
        ],
      },
      vendorTable: {
        headers: ["Category", "Amount"],
        rows: [
          ["Production Cost", formatCurrency(eventDetail.finances?.production_cost ?? 0)],
          ["Marketing Cost", formatCurrency(eventDetail.finances?.marketing_cost ?? 0)],
          ["Artist Fee", formatCurrency(eventDetail.finances?.artist_fee ?? 0)],
          ["Projected Revenue", formatCurrency(projectedRevenue)],
        ],
      },
      riskTable: {
        headers: ["Risk", "Current State", "Severity"],
        rows:
          riskAssessment && riskAssessment.risks.length > 0
            ? riskAssessment.risks.map((r) => [
                String(r.risk ?? ""),
                String(r.state ?? ""),
                String(r.severity ?? "low"),
              ])
            : [
                ["Break-even Attainment", ticketsSold < breakEvenTickets ? "Below target" : "On track", "low"],
                ["Profitability", actualProfit < 0 ? "Negative" : "Positive", actualProfit < 0 ? "high" : "low"],
                ["Ticket Velocity", `${ticketsSold.toLocaleString("en-IN")} sold`, "low"],
              ],
      },
      expenseChart: [
        { name: "Production", value: eventDetail.finances?.production_cost ?? 0 },
        { name: "Marketing", value: eventDetail.finances?.marketing_cost ?? 0 },
        { name: "Artist", value: eventDetail.finances?.artist_fee ?? 0 },
      ],
      revenueChart: revenueChartPoints,
      ticketTierChart: ticketTierPoints,
      scenarioChart: scenarioChartRows,
      scenarioMetrics: [
        { label: "Projected Revenue", value: formatCurrency(projectedRevenue), tone: toneRevenueAmount(projectedRevenue) },
        { label: "Total Cost", value: formatCurrency(totalCost), tone: toneCostAmount(totalCost) },
        { label: "Projected Profit", value: formatCurrency(projectedProfit), tone: toneSignedAmount(projectedProfit) },
      ],
      reportMetrics: [
        { label: "Revenue (actual)", value: formatCurrency(actualRevenue), tone: toneRevenueAmount(actualRevenue) },
        { label: "Expenses", value: formatCurrency(totalCost), tone: toneCostAmount(totalCost) },
        { label: "Net Profit (actual)", value: formatCurrency(actualProfit), tone: toneSignedAmount(actualProfit) },
        { label: "Revenue (projected)", value: formatCurrency(projectedRevenue), tone: toneRevenueAmount(projectedRevenue) },
        { label: "Net Profit (projected)", value: formatCurrency(projectedProfit), tone: toneSignedAmount(projectedProfit) },
      ],
      alerts: [
        {
          id: "break-even-alert",
          level: ticketsSold < breakEvenTickets ? "warning" : "info",
          title: "Break-even watch",
          subtitle: `${ticketsSold.toLocaleString("en-IN")} / ${breakEvenTickets.toLocaleString("en-IN")} tickets`,
        },
      ],
      insights: [
        {
          id: "profit-insight",
          title: "Profitability",
          text: `Realized profit so far is ${formatCurrency(actualProfit)} at ${formatPercent(sellThrough)} sell-through. Projection at full sell-through is ${formatCurrency(projectedProfit)}.`,
          confidence: "Source: ticket sales + event finances",
        },
      ],
    };
  },
};

function buildTicketEntryQuery(params?: TicketEntryLogListParams): string {
  const qs = new URLSearchParams();
  if (!params) return "";
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.set(key, String(value));
    }
  });
  const query = qs.toString();
  return query ? `?${query}` : "";
}

export const ticketEntriesApi = {
  list: (eventId: string, params?: TicketEntryLogListParams, token?: string) =>
    request<TicketEntryLogListResponse>(
      getApiUrl(`${API_ENDPOINTS.TICKET_ENTRIES.LIST(eventId)}${buildTicketEntryQuery(params)}`),
      { method: "GET", headers: getAuthHeaders(token) },
    ),
  create: (eventId: string, body: TicketEntryLogCreate, token?: string) =>
    request<TicketEntryLog>(getApiUrl(API_ENDPOINTS.TICKET_ENTRIES.CREATE(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
  update: (eventId: string, entryId: string, body: TicketEntryLogUpdate, token?: string) =>
    request<TicketEntryLog>(getApiUrl(API_ENDPOINTS.TICKET_ENTRIES.UPDATE(eventId, entryId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
};

export type TicketScanResultCode =
  | "admitted"
  | "already_entered"
  | "invalid"
  | "wrong_event"
  | "cancelled"
  | "payment_due"
  // Activity tickets (one QR, several slots): which slot to admit is ticketing's call
  | "choose_slot"
  | "not_open"
  | "unavailable";

/** A slot the gate operator can choose when two of a ticket's slots are running at once. */
export type TicketScanSlotOption = {
  activity_slot_id: number;
  activity_name: string;
  starts_at: string;
  ends_at: string;
  checked_in?: boolean;
};

export type TicketScanResponse = {
  result: TicketScanResultCode;
  message: string;
  /** Present for `choose_slot`. */
  options?: TicketScanSlotOption[];
  ticket: {
    ticket_number: string;
    booking_number: string | null;
    tier_name: string | null;
    seat_label: string | null;
    holder_name: string | null;
    holder_phone: string | null;
    checked_in_at: string | null;
    checked_in_by: string | null;
    status?: string;
    issuer?: string;
    admits?: number;
    /** Rupees still to collect at the gate (0 = nothing due) */
    amount_due?: number;
    /** Activity tickets: every slot on the ticket and whether it has been used. */
    activities?: {
      activity_slot_id: number | null;
      activity_name: string;
      starts_at: string | null;
      ends_at: string | null;
      checked_in_at: string | null;
    }[];
  } | null;
};

export type TicketScanStats = {
  total_tickets: number;
  checked_in: number;
  recent: {
    ticket_number: string;
    holder_name: string | null;
    tier_name: string | null;
    checked_in_at: string;
    checked_in_by: string | null;
  }[];
};

export const ticketScanApi = {
  scan: (eventId: string, tokenValue: string, token?: string, activitySlotId?: number) =>
    request<TicketScanResponse>(getApiUrl(API_ENDPOINTS.TICKET_SCAN.SCAN(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify({
        token: tokenValue,
        ...(activitySlotId != null ? { activity_slot_id: activitySlotId } : {}),
      }),
    }),
  stats: (eventId: string, token?: string) =>
    request<TicketScanStats>(getApiUrl(API_ENDPOINTS.TICKET_SCAN.STATS(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  /** Gate took cash/UPI for an unpaid pass: records the sale, then admits. */
  collect: (eventId: string, tokenValue: string, mode: "cash" | "upi", token?: string) =>
    request<TicketScanResponse>(getApiUrl(API_ENDPOINTS.TICKET_SCAN.COLLECT(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify({ token: tokenValue, mode }),
    }),
};

export type PublicPass = {
  state: "valid" | "checked_in" | "payment_due" | "void";
  qr_token: string | null;
  ticket_number: string;
  holder_name: string | null;
  admits: number;
  tier_label: string | null;
  amount_due: number;
  event_name: string;
  event_date: string | null;
  event_time: string | null;
  venue: string | null;
  city: string | null;
};

export type GuestStatus = {
  name: string;
  payment_status: "none" | "pending" | "paid" | "due" | "refunded";
  pay_preference: "online" | "gate" | null;
  event_name: string | null;
  event_date: string | null;
  pass_state: PublicPass["state"] | null;
  pass_url: string | null;
};

/** Public GET with no Authorization header: a guest's pass link must never trigger staff auth recovery. */
async function publicGet<T>(url: string): Promise<T> {
  const res = await fetch(url, { method: "GET" });
  const text = await res.text();
  if (!res.ok) {
    throw new ApiError(formatApiErrorMessage(text, res.status), res.status, parseErrorDetail(text));
  }
  return JSON.parse(text) as T;
}

export const publicPassApi = {
  pass: (token: string) => publicGet<PublicPass>(getApiUrl(API_ENDPOINTS.PUBLIC_PASS.PASS(token))),
  guestStatus: (token: string) => publicGet<GuestStatus>(getApiUrl(API_ENDPOINTS.PUBLIC_PASS.GUEST_STATUS(token))),
};

export type ProvisionTicketingResult = {
  link_id: string;
  ticketing_event_id: number;
  provisioned: boolean;
  tier_links: { ticket_tier_id: string; ticketing_category_id: number }[];
};

export const ticketingProvisionApi = {
  /** Sell on Zoniq: create the unlisted Ticketing event for this event and link it. */
  provision: (eventId: string, token?: string) =>
    request<ProvisionTicketingResult>(getApiUrl(API_ENDPOINTS.TICKETING_PROVISION.PROVISION(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
    }),
};

export type ActivityAnalyticsSlot = {
  activity_id: number | null;
  activity_name: string;
  activity_slot_id: number | null;
  starts_at: string | null;
  ends_at: string | null;
  sold: number;
  revenue: number;
  checked_in: number;
  capacity: number | null;
  fill_rate: number | null;
  check_in_rate: number | null;
};

export type ActivityAnalytics = {
  summary: {
    tickets: number;
    activity_entries: number;
    revenue: number;
    bundle_tickets: number;
    individual_tickets: number;
    bundle_share: number | null;
    checked_in_entries: number;
    check_in_rate: number | null;
  };
  by_activity: {
    activity_id: number | null;
    activity_name: string;
    sold: number;
    tickets: number;
    revenue: number;
    checked_in: number;
    capacity: number | null;
    fill_rate: number | null;
    check_in_rate: number | null;
  }[];
  by_slot: ActivityAnalyticsSlot[];
  popular_slots: ActivityAnalyticsSlot[];
  capacity_known: boolean;
};

/** Analysis of events sold by activity on zoniq.in (activities are managed in the ticketing dashboard). */
export const activityAnalyticsApi = {
  get: (eventId: string, token?: string) =>
    request<ActivityAnalytics>(getApiUrl(API_ENDPOINTS.ACTIVITY_ANALYTICS.GET(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
};

export const ticketingPartnersApi = {
  list: (token?: string) =>
    request<TicketingPartner[]>(getApiUrl(API_ENDPOINTS.TICKETING_PARTNERS.LIST), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
};

export const teamMembersApi = {
  list: (token?: string) =>
    request<TeamMember[]>(getApiUrl(API_ENDPOINTS.TEAM_MEMBERS.LIST), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (body: TeamMemberCreate, token?: string) =>
    request<TeamMember>(getApiUrl(API_ENDPOINTS.TEAM_MEMBERS.CREATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
  update: (memberId: string, body: TeamMemberUpdate, token?: string) =>
    request<TeamMember>(getApiUrl(API_ENDPOINTS.TEAM_MEMBERS.UPDATE(memberId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
  remove: (memberId: string, token?: string) =>
    request<void>(getApiUrl(API_ENDPOINTS.TEAM_MEMBERS.DELETE(memberId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
};

export const companyOperatorsApi = {
  list: (token?: string) =>
    request<CompanyOperatorAccount[]>(getApiUrl(API_ENDPOINTS.COMPANY_OPERATORS.LIST), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (body: CompanyOperatorCreate, token?: string) =>
    request<CompanyOperatorAccount>(getApiUrl(API_ENDPOINTS.COMPANY_OPERATORS.CREATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
  resetCredentials: (userId: string, body: CompanyOperatorCredentialsPatch, token?: string) =>
    request<CompanyOperatorAccount>(getApiUrl(API_ENDPOINTS.COMPANY_OPERATORS.CREDENTIALS(userId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
};

export const eventPromoterAccountsApi = {
  list: (eventId: string, token?: string) =>
    request<EventPromoterAssignment[]>(getApiUrl(API_ENDPOINTS.EVENT_PROMOTER_ACCOUNTS.LIST(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  create: (eventId: string, body: EventPromoterAccountCreate, token?: string) =>
    request<EventPromoterAssignmentCreateResult>(getApiUrl(API_ENDPOINTS.EVENT_PROMOTER_ACCOUNTS.CREATE(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
  remove: (eventId: string, promoterUserId: string, token?: string) =>
    request<void>(getApiUrl(API_ENDPOINTS.EVENT_PROMOTER_ACCOUNTS.DELETE(eventId, promoterUserId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
};

export const eventNotesApi = {
  get: (eventId: string, token?: string) =>
    request<EventNote>(getApiUrl(API_ENDPOINTS.EVENT_NOTES.DETAIL(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  upsert: (eventId: string, body: string, token?: string) =>
    request<EventNote>(getApiUrl(API_ENDPOINTS.EVENT_NOTES.DETAIL(eventId)), {
      method: "PUT",
      headers: getAuthHeaders(token),
      body: JSON.stringify({ body }),
    }),
};

export const eventDealTermsApi = {
  /** 404 (no deal terms saved yet) resolves to null rather than throwing.
   *
   * `promoterUserId` picks which row to read: omitted is the event-wide row,
   * a uuid is that promoter's. The backend ignores it for a promoter caller
   * and forces their own id, so a promoter can only ever read their own. */
  get: async (eventId: string, token?: string, promoterUserId?: string | null): Promise<EventDealTerms | null> => {
    const qs = promoterUserId ? `?promoter_user_id=${encodeURIComponent(promoterUserId)}` : "";
    try {
      return await request<EventDealTerms>(getApiUrl(API_ENDPOINTS.EVENT_DEAL_TERMS.DETAIL(eventId)) + qs, {
        method: "GET",
        headers: getAuthHeaders(token),
      });
    } catch (err) {
      if (err instanceof Error && /deal terms not found/i.test(err.message)) return null;
      throw err;
    }
  },
  upsert: (eventId: string, body: EventDealTermsUpsert, token?: string) =>
    request<EventDealTerms>(getApiUrl(API_ENDPOINTS.EVENT_DEAL_TERMS.DETAIL(eventId)), {
      method: "PUT",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
};

export const eventFilesApi = {
  list: (eventId: string, token?: string) =>
    request<EventFile[]>(getApiUrl(API_ENDPOINTS.EVENT_FILES.LIST(eventId)), {
      method: "GET",
      headers: getAuthHeaders(token),
    }),
  upload: (eventId: string, file: File, token?: string): Promise<EventFile> => {
    const body = new FormData();
    body.append("file", file);
    return request<EventFile>(getApiUrl(API_ENDPOINTS.EVENT_FILES.LIST(eventId)), {
      method: "POST",
      headers: authHeadersMultipart(token),
      body,
    });
  },
  remove: (eventId: string, fileId: string, token?: string) =>
    request<void>(getApiUrl(API_ENDPOINTS.EVENT_FILES.DELETE(eventId, fileId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  /** Follows the backend's redirect to a presigned S3 URL and returns the file bytes. */
  download: (eventId: string, fileId: string, token?: string): Promise<Blob> =>
    fetchBlobWithAuth(getApiUrl(API_ENDPOINTS.EVENT_FILES.DOWNLOAD(eventId, fileId)), token),
};

export const adminTicketingLinksApi = {
  list: (token?: string) =>
    request<TicketingLink[]>(getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.LIST), {
      headers: getAuthHeaders(token),
    }),
  companies: (token?: string) =>
    request<AdminCompanySummary[]>(getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.COMPANIES), {
      headers: getAuthHeaders(token),
    }),
  companyEvents: (companyId: string, token?: string) =>
    request<AdminCompanyEvent[]>(
      getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.COMPANY_EVENTS(companyId)),
      { headers: getAuthHeaders(token) }
    ),
  eventOsEvents: (q?: string, token?: string) => {
    const base = getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.EVENT_OS_EVENTS);
    const url = q ? `${base}?q=${encodeURIComponent(q)}` : base;
    return request<AdminCompanyEvent[]>(url, { headers: getAuthHeaders(token) });
  },
  ticketingEvents: (q?: string, token?: string) => {
    const base = getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.TICKETING_EVENTS);
    const url = q ? `${base}?q=${encodeURIComponent(q)}` : base;
    return request<TicketingCatalogEvent[]>(url, { headers: getAuthHeaders(token) });
  },
  ticketingEvent: (id: number, token?: string) =>
    request<TicketingCatalogEvent>(
      getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.TICKETING_EVENT(id)),
      { headers: getAuthHeaders(token) }
    ),
  create: (
    body: {
      event_id: string;
      ticketing_event_id: number;
      tier_mappings: TicketingTierMapping[];
      backfill?: boolean;
    },
    token?: string
  ) =>
    request<TicketingLink>(getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.CREATE), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    }),
  unlink: (linkId: string, token?: string) =>
    request<TicketingLink>(getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.UNLINK(linkId)), {
      method: "DELETE",
      headers: getAuthHeaders(token),
    }),
  backfill: (linkId: string, token?: string) =>
    request<{ enqueued?: number }>(getApiUrl(API_ENDPOINTS.ADMIN_TICKETING_LINKS.BACKFILL(linkId)), {
      method: "POST",
      headers: getAuthHeaders(token),
    }),
};

/** Invite-only events: the organizer's Invites tab plus the public request form and pay link. */
export const invitesApi = {
  getForm: (eventId: string, token?: string) =>
    request<InviteForm>(getApiUrl(API_ENDPOINTS.INVITES.FORM(eventId)), { headers: getAuthHeaders(token) }),
  updateForm: (eventId: string, payload: InviteFormPatch, token?: string) =>
    request<InviteForm>(getApiUrl(API_ENDPOINTS.INVITES.FORM(eventId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  regenerateLink: (eventId: string, token?: string) =>
    request<InviteForm>(getApiUrl(API_ENDPOINTS.INVITES.REGENERATE_LINK(eventId)), {
      method: "POST",
      headers: getAuthHeaders(token),
    }),
  listRequests: (eventId: string, token?: string) =>
    request<InviteRequestList>(getApiUrl(API_ENDPOINTS.INVITES.REQUESTS(eventId)), { headers: getAuthHeaders(token) }),
  approve: (eventId: string, requestId: string, force = false, token?: string) =>
    request<InviteRequest>(getApiUrl(API_ENDPOINTS.INVITES.APPROVE(eventId, requestId)), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify({ force }),
    }),
  waitlist: (eventId: string, requestId: string, token?: string) =>
    request<InviteRequest>(getApiUrl(API_ENDPOINTS.INVITES.WAITLIST(eventId, requestId)), {
      method: "POST",
      headers: getAuthHeaders(token),
    }),
  updateRequest: (eventId: string, requestId: string, payload: { admin_notes: string | null }, token?: string) =>
    request<InviteRequest>(getApiUrl(API_ENDPOINTS.INVITES.REQUEST(eventId, requestId)), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    }),
  exportCsv: async (eventId: string, status: string, token?: string): Promise<Blob> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.INVITES.EXPORT(eventId, status)), {
      headers: getAuthHeaders(token),
    });
    if (!response.ok) throw new Error(formatApiErrorMessage(await response.text(), response.status));
    return response.blob();
  },
  publicMeta: (formToken: string) => request<PublicInviteMeta>(getApiUrl(API_ENDPOINTS.INVITES.PUBLIC(formToken))),
  publicSubmit: (formToken: string, payload: InviteRequestSubmit) =>
    request<InviteSubmitResult>(getApiUrl(API_ENDPOINTS.INVITES.PUBLIC(formToken)), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }),
  payInfo: (payToken: string) => request<InvitePayInfo>(getApiUrl(API_ENDPOINTS.INVITES.PUBLIC_PAY(payToken))),
  startPayment: (payToken: string) =>
    request<InvitePayInfo>(getApiUrl(API_ENDPOINTS.INVITES.PUBLIC_PAY(payToken)), { method: "POST" }),
  myStatus: (formToken: string, accessToken: string) =>
    request<InviteStatusView>(getApiUrl(API_ENDPOINTS.INVITES.PUBLIC_ME(formToken, accessToken))),
  myPay: (formToken: string, accessToken: string) =>
    request<InvitePayInfo>(getApiUrl(API_ENDPOINTS.INVITES.PUBLIC_ME_PAY(formToken)), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ access_token: accessToken }),
    }),
  sendLoginCode: (formToken: string, phone: string) =>
    request<{ detail: string }>(getApiUrl(API_ENDPOINTS.INVITES.PUBLIC_LOOKUP(formToken)), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone }),
    }),
  verifyLoginCode: (formToken: string, phone: string, code: string) =>
    request<{ access_token: string }>(getApiUrl(API_ENDPOINTS.INVITES.PUBLIC_VERIFY(formToken)), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, code }),
    }),
};
